import React, {
  useCallback, useEffect, useRef, useState, useMemo
} from 'react';
import {
  shallowEqual, useDispatch, useSelector
} from 'react-redux';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { answerSurveyQuestionAction } from '../../../actions';
import { SURVEY_TITLE, routes } from '../../../constants';
import {
  autoFocusElementById,
  getCompiledPath,
  getSurveyQuestions,
  getSurveySchemaPartQuestions
} from '../../../util';
import { getTranslation, withLang, langType } from '../../../i18n';
import surveyStyles from '../survey.module.css';
import SurveyProgressBar from '../SurveyProgressBar';
import SubmitSurveyButton from '../SubmitSurveyButton';
import SurveyPartItem from '../SurveyPartItem';
import ErrorSummary from '../../ErrorSummary';

const getQuestionOrderById = (questions) =>
  questions.reduce((acc, question, idx) => {
    acc[question.id] = idx + 1;
    return acc;
  }, []);

const getExtraQuestions = (survey) => {
  if (survey && survey.extraSurveyPart) {
    return getSurveySchemaPartQuestions(survey.extraSurveyPart);
  }
  return [];
};

const getSurveySchemaPartsWithExtraPart = ({ surveySchemaParts, extraSurveyPart }) => {
  if (extraSurveyPart) {
    return [...surveySchemaParts, extraSurveyPart];
  }
  return surveySchemaParts;
};

const scrollToSurveyTitle = (surveyTitle) => {
  const titleElement = document.getElementById(surveyTitle);
  if (titleElement) {
    titleElement.scrollIntoView();
  }
};

const PANEL_TITLE = 'panel_title';

const SurveyPart = ({ translate: t, lang }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const surveyPage = Number(params.page);

  const survey = useSelector((state) => state.survey.surveyContent, shallowEqual);
  const questionResponses = useSelector((state) => state.survey.questionResponses, shallowEqual);

  const surveySchemaParts = useMemo(() => getSurveySchemaPartsWithExtraPart(survey), [survey]);
  const allQuestions = useMemo(() => [...getSurveyQuestions(survey), ...getExtraQuestions(survey)], [survey]);
  const questionOrderNumById = useMemo(() => (
    getQuestionOrderById(allQuestions)
  ), [allQuestions]);

  const surveyPart = surveySchemaParts[surveyPage - 1];
  const schemaPartAmount = surveySchemaParts.length;

  const [setCurrentPage, setPageCount] = useOutletContext();
  const formRef = useRef(null);

  const [pageErrors, setPageErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    autoFocusElementById(PANEL_TITLE);
    scrollToSurveyTitle(SURVEY_TITLE);
  }, []);

  const handleResponseChange = useCallback(
    (questionId, questionResponse) => {
      dispatch(answerSurveyQuestionAction(questionId, questionResponse));
    }, []
  );

  const getQuestionAnswer = useCallback(
    (question) => {
      if (questionResponses[question.id]) {
        return questionResponses[question.id].questionResponse;
      }
      return null;
    },
    [questionResponses]
  );

  const questionIsInErrorState = useCallback(
    (q) => q.required && showErrors && !questionResponses[q.id],
    [questionResponses, showErrors]
  );

  const collectErrorsForPage = useCallback(() => {
    if (!surveyPart) return [];

    const questions = getSurveySchemaPartQuestions(surveyPart);
    const missing = questions.filter((q) => q.required && !questionResponses[q.id]);

    return missing.map((q) => ({
      id: q.id,
      linkText: `${getTranslation(q.text, lang)}: ${t('surveyParts.isRequired')}`
    }));
  }, [surveyPart, questionResponses, lang, t]);

  const onContinue = useCallback((event) => {
    const errors = collectErrorsForPage();
    setShowErrors(true); // Only show errors after submit attempt
    setPageErrors(errors);
    if (errors.length > 0) {
      event.preventDefault();
    }
  }, [collectErrorsForPage]);

  const jumpToError = useCallback((error) => {
    if (!formRef.current) return;
    const inputElement = formRef.current.querySelector(`[name="${error.id}"]`);
    if (!inputElement) return;
    inputElement.focus();
  }, []);

  const renderSurveyPart = useCallback(
    () =>
      surveyPart?.surveySchemaPartItems.map((schemaPartItem) => (
        <div key={schemaPartItem.id}>
          <SurveyPartItem
            schemaPartItem={schemaPartItem}
            questionOrderNumById={questionOrderNumById}
            lang={lang}
            handleResponseChange={handleResponseChange}
            getQuestionAnswer={getQuestionAnswer}
            getErrorState={questionIsInErrorState}
            translate={t}
          />
        </div>
      )),
    [lang, t, handleResponseChange, getQuestionAnswer, questionIsInErrorState, questionOrderNumById, surveyPart]
  );

  const renderNavLinks = useCallback(
    (currentPage, pageCount) => {
      const { id: surveyId, researchConsentNeeded } = survey;

      const prevPage = currentPage === 1
        ? getCompiledPath(routes.survey.introduction, { surveyId })
        : getCompiledPath(routes.survey.surveyPart, { surveyId, page: currentPage - 1 });
      const nextPage = currentPage < pageCount
        ? getCompiledPath(routes.survey.surveyPart, { surveyId, page: currentPage + 1 })
        : getCompiledPath(routes.survey.researchConsent, { surveyId });

      return (
        <nav className={surveyStyles.navigation}>
          <Link
            className={`${surveyStyles.navButton} button--action icon--arrow-left`}
            aria-controls="progress-tracker"
            to={prevPage}
          >
            {t('common.previous')}
          </Link>
          {currentPage < pageCount || researchConsentNeeded
            ? (
              <Link
                className={`${surveyStyles.navButton} button--action icon--arrow-right`}
                aria-controls="progress-tracker"
                to={nextPage}
                onClick={(e) => onContinue(e)}
              >
                {t('common.continue')}
              </Link>
            )
            : (
              <SubmitSurveyButton canSubmit={() => {
                const errs = collectErrorsForPage();
                setPageErrors(errs);
                return errs.length === 0;
              }}
              />
            )}
        </nav>
      );
    },
    [t, survey, onContinue, collectErrorsForPage]
  );

  const renderTitle = useCallback(() => {
    const surveyTitlePart = surveyPart?.title
      ? `: ${getTranslation(surveyPart?.title, lang)}`
      : '';

    return (
      <div className="flex-inline">
        <h2 id={PANEL_TITLE} className="panel__title">
          {`${t('survey.part')} ${surveyPage}${surveyTitlePart}`}
        </h2>
      </div>
    );
  }, [t, lang, surveyPage, surveyPart]);

  return (
    <form className="form" ref={formRef} onSubmit={(e) => e.preventDefault()}>
      <SurveyProgressBar
        current={surveyPage}
        total={schemaPartAmount}
        researchConsentNeeded={survey.researchConsentNeeded}
        setCurrentPage={setCurrentPage}
        setPageCount={setPageCount}
        translate={t}
      />
      {renderTitle()}
      <div className={surveyStyles.requiredQuestionInfo} aria-hidden>
        <p>{t('requiredQuestionInfo')}</p>
      </div>
      {showErrors && (
        <ErrorSummary
          title={`${t('errorSummary.heading')}:`}
          errors={pageErrors}
          onJump={jumpToError}
        />
      )}

      {renderSurveyPart()}
      {renderNavLinks(surveyPage, schemaPartAmount)}
    </form>
  );
};

SurveyPart.propTypes = {
  ...langType
};

export default withLang(SurveyPart);
